/**
 * Page Performance — small shared pieces used by every tab: the status
 * badge, the panel card and its loading / empty / error states, the figure
 * tile with its compare line, the ⓘ button, the page-name cell and the
 * trend cell (sparkline or the trend in words).
 *
 * Colours come from the app's tokens (perf-tones.js); every figure is
 * formatted by the view-model helpers, which go through lib/format.js.
 */
import { AlertTriangle, Info, Loader2, RefreshCcw } from 'lucide-react'
import {
  STATUS_LABEL,
  formatMs,
  formatPercent,
  levelFor,
  pageNameForRoute,
  sparkPath,
  trendInWords,
} from '../../lib/page-perf/view-model'
import { NEUTRAL_TONE, tintOf, toneOf } from './perf-tones'

const cx = (...a) => a.filter(Boolean).join(' ')

/** The verdict badge — tinted chip, coloured dot, readable text (both themes). */
export function PerfStatusBadge({ status, className = '' }) {
  const label = STATUS_LABEL[status]
  const tone = label ? toneOf(status) : NEUTRAL_TONE
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold text-[color:var(--color-ink-strong)]',
        className,
      )}
      style={{ backgroundColor: tintOf(tone), borderColor: tintOf(tone, 40) }}
      title={label ? undefined : 'Not judged on this server yet'}
    >
      <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: tone }} />
      {label ?? 'Not judged'}
    </span>
  )
}

/** A coloured dot for chips and legends. */
export function ToneDot({ tone, className = '' }) {
  return (
    <span
      aria-hidden="true"
      className={cx('inline-block h-2.5 w-2.5 shrink-0 rounded-full', className)}
      style={{ backgroundColor: tone }}
    />
  )
}

/** A panel: title, a note under it (what it is a share of, which zone…), actions. */
export function PerfCard({ title, note, actions, children, className = '', id }) {
  return (
    <section
      id={id}
      className={cx(
        'min-w-0 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 sm:p-5',
        className,
      )}
    >
      {title || actions ? (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {title ? (
              <h3 className="m-0 font-display text-base font-semibold text-[color:var(--color-ink-strong)]">{title}</h3>
            ) : null}
            {note ? <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)]">{note}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  )
}

/** Skeleton lines while a panel loads for the first time. */
export function PanelLoading({ lines = 4, label = 'Loading…' }) {
  return (
    <div role="status" aria-label={label} className="space-y-2.5 py-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 animate-pulse rounded bg-[color:var(--color-rule)]"
          style={{ width: `${55 + ((i * 17) % 40)}%` }}
        />
      ))}
    </div>
  )
}

/** A dashed empty state that names the scope it is empty for. */
export function PanelEmpty({ title, body, icon: Icon = Info }) {
  return (
    <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] px-4 py-8 text-center">
      <span className="mx-auto mb-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]">
        <Icon size={16} />
      </span>
      <p className="m-0 text-sm font-semibold text-[color:var(--color-ink-strong)]">{title}</p>
      {body ? <p className="mx-auto mt-1 max-w-md text-[13px] text-[color:var(--color-ink-muted)]">{body}</p> : null}
    </div>
  )
}

/** An error box with the message and a retry. */
export function PanelError({ message, onRetry }) {
  return (
    <div role="alert" className="rounded-lg border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] px-4 py-5 text-center">
      <p className="m-0 inline-flex items-center gap-1.5 text-sm text-[color:var(--color-danger-text)]">
        <AlertTriangle size={14} /> {message || 'These figures could not be loaded.'}
      </p>
      {onRetry ? (
        <div>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-[color:var(--color-accent-strong)] hover:underline"
          >
            <RefreshCcw size={13} /> Try again
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Small spinner shown while a FILTER change reloads (live ticks stay silent). */
export function FilterSpinner({ show, label = 'Loading the new filter…' }) {
  if (!show) return null
  return (
    <span role="status" className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)]">
      <Loader2 size={13} className="animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </span>
  )
}

/** The ⓘ button beside a figure. Stops the click reaching a clickable row. */
export function InfoDot({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick?.()
      }}
      aria-label={label}
      title={label}
      className="inline-flex min-h-[24px] min-w-[24px] shrink-0 cursor-pointer items-center justify-center rounded-full text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-accent-bg)]"
    >
      <Info size={14} />
    </button>
  )
}

/** "▲ 9% vs 468 ms" — green when the move is an improvement, red when not. */
export function DeltaLine({ delta, vs, suffix }) {
  if (!delta) {
    return <span className="text-[color:var(--color-ink-subtle)]">no figure to compare with{suffix ? ` · ${suffix}` : ''}</span>
  }
  const colour =
    delta.good === true
      ? 'var(--color-success)'
      : delta.good === false
        ? 'var(--color-danger-text)'
        : 'var(--color-ink-muted)'
  return (
    <span>
      <span className="font-semibold tabular-nums" style={{ color: colour }}>
        {delta.text}
      </span>{' '}
      <span>vs {vs}</span>
      {suffix ? <span> · {suffix}</span> : null}
    </span>
  )
}

/**
 * A figure tile: label (plain name first, code in brackets), the figure,
 * its meaning, and the compare line. `tone` colours the figure when it is
 * judged against a limit.
 */
export function FigureTile({ label, icon: Icon, value, tone, meaning, compare, info, title }) {
  return (
    <div
      className="flex min-w-0 flex-col rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4"
      title={title}
      style={tone ? { boxShadow: `inset 3px 0 0 ${tone}` } : undefined}
    >
      <div className="flex items-center gap-2 text-[13px] font-medium text-[color:var(--color-ink-muted)]">
        {Icon ? (
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]">
            <Icon size={14} />
          </span>
        ) : null}
        <span className="min-w-0">{label}</span>
        {info}
      </div>
      <p className="mb-0 mt-2 font-display text-2xl font-semibold leading-none tabular-nums text-[color:var(--color-ink-strong)]">
        {value}
      </p>
      {meaning ? <p className="mb-0 mt-2 text-xs leading-snug text-[color:var(--color-ink-muted)]">{meaning}</p> : null}
      {compare ? <p className="mb-0 mt-1.5 text-xs leading-snug text-[color:var(--color-ink-muted)]">{compare}</p> : null}
    </div>
  )
}

/**
 * The page-name cell: the app's own title over the route. The column has a
 * stated width (DataTable `minWidth`); the INNER lines clip, never the cell.
 */
export function PageNameCell({ route }) {
  const name = pageNameForRoute(route)
  return (
    <div className="min-w-0 max-w-[260px]">
      <p className="m-0 truncate font-semibold text-[color:var(--color-ink-strong)]" title={name}>
        {name}
      </p>
      <p className="m-0 truncate font-mono text-xs text-[color:var(--color-ink-muted)]" title={route}>
        {route}
      </p>
    </div>
  )
}

const numOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** A muted placeholder ("—") inside a table cell. */
export function Muted({ children }) {
  return <span className="text-[color:var(--color-ink-subtle)]">{children}</span>
}

/** P95 figure with a meter against the critical limit, coloured by its level. */
export function P95Cell({ row, limits, onInfo }) {
  const v = numOrNull(row.p95Ms)
  if (v == null) return <Muted>—</Muted>
  const crit = limits?.load?.critical
  const level = levelFor(v, limits?.load)
  const pct = crit ? Math.min(100, Math.max(3, (v / crit) * 100)) : null
  return (
    <div className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex items-center gap-0.5">
        <b className="tabular-nums text-[color:var(--color-ink-strong)]">{formatMs(v)}</b>
        {onInfo ? <InfoDot onClick={() => onInfo(row)} label="How the load times are worked out" /> : null}
      </span>
      {pct != null ? (
        <span aria-hidden="true" className="block h-1.5 w-20 overflow-hidden rounded-full bg-[color:var(--color-rule)]">
          <span className="block h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: toneOf(level) }} />
        </span>
      ) : null}
    </div>
  )
}

/** A failed-request rate, red when it is over a limit. */
export function ErrorsCell({ value, limits }) {
  const v = numOrNull(value)
  if (v == null) return <Muted>—</Muted>
  const level = levelFor(v, limits?.errors)
  const bad = level && level !== 'healthy'
  return (
    <span
      className={'tabular-nums ' + (bad ? 'font-semibold text-[color:var(--color-danger-text)]' : 'text-[color:var(--color-ink-muted)]')}
      title={bad ? `Over the ${STATUS_LABEL[level]} limit` : undefined}
    >
      {formatPercent(v)}
    </span>
  )
}

/** Sparkline width — stated, so a narrow layout can never collapse it. */
const SPARK_W = 88
const SPARK_H = 24

/** The trend cell: a real-width sparkline, or why there is no line. */
export function TrendCell({ trend, slot, status }) {
  const t = trendInWords(trend, slot)
  if (t.kind !== 'line') {
    return (
      <div className="min-w-[130px] text-xs text-[color:var(--color-ink-muted)]">
        <p className="m-0">{t.text}</p>
        {t.sub ? <p className="m-0 text-[color:var(--color-ink-subtle)]">{t.sub}</p> : null}
      </div>
    )
  }
  const d = sparkPath(
    t.points.map((p) => p.p95Ms),
    SPARK_W,
    SPARK_H,
  )
  return (
    <svg
      width={SPARK_W}
      height={SPARK_H}
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      style={{ width: SPARK_W, minWidth: SPARK_W }}
      role="img"
      aria-label={t.text}
    >
      <title>{t.text}</title>
      <path d={d} fill="none" stroke={toneOf(status)} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
